import { useEffect, useState } from 'react';
import {
  type DocumentData,
  type QueryConstraint,
  collection,
  doc,
  onSnapshot,
  query,
  queryEqual,
} from 'firebase/firestore';
import { db } from '../config/firebase';

interface FirestoreState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

const EMPTY_CONSTRAINTS: QueryConstraint[] = [];

export function useCollection<T = DocumentData>(
  collectionName: string,
  queryConstraints?: QueryConstraint[],
) {
  const [state, setState] = useState<FirestoreState<T[]>>({
    data: null,
    loading: true,
    error: null,
  });

  // Referencia estable por IGUALDAD ESTRUCTURAL (queryEqual), no por identidad.
  // Los constraints inline (`[orderBy(...)]`) crean objetos nuevos en cada
  // render; comparar `c !== prev[i]` siempre daba "cambió", el efecto se
  // re-suscribía en cada render y cada re-suscripción emitía primero el CACHÉ
  // de Firestore. El setState de esa emisión disparaba otro render → otra
  // re-suscripción → otra emisión de caché…: el listener se derribaba antes de
  // recibir la respuesta del servidor y la lista quedaba atascada en los pocos
  // documentos cacheados (p. ej. 1–2 usuarios en /admin/medicos) hasta un
  // refresh manual.
  const constraints = queryConstraints ?? EMPTY_CONSTRAINTS;
  const nextQuery = query(collection(db, collectionName), ...constraints);
  const [currentQuery, setCurrentQuery] = useState(nextQuery);
  let stableQuery = currentQuery;
  if (!queryEqual(currentQuery, nextQuery)) {
    stableQuery = nextQuery;
    setCurrentQuery(nextQuery);
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(
      stableQuery,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as T[];
        setState({ data: docs, loading: false, error: null });
      },
      (error) => {
        setState({ data: null, loading: false, error });
      },
    );

    return unsubscribe;
  }, [stableQuery]);

  return state;
}

export function useDocument<T = DocumentData>(
  collectionName: string,
  docId: string | undefined,
) {
  const [state, setState] = useState<FirestoreState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!docId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ data: null, loading: false, error: null });
      return;
    }

    const ref = doc(db, collectionName, docId);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (snapshot.exists()) {
          setState({
            data: { id: snapshot.id, ...snapshot.data() } as T,
            loading: false,
            error: null,
          });
        } else {
          setState({ data: null, loading: false, error: null });
        }
      },
      (error) => {
        setState({ data: null, loading: false, error });
      },
    );

    return unsubscribe;
  }, [collectionName, docId]);

  return state;
}
